// Sidebar nav — ported from design's sidebar.jsx with hash-router nav targets.
import { useState } from 'react';
import { PALETTE } from '../palette.js';
import { tenantRouteDecision } from '../product-policy.js';
import BrandIdentity from './BrandIdentity.jsx';
import { Pill, LiveDot } from './primitives.jsx';
import {
  IconHome, IconChart, IconUsers, IconVideo, IconQuiz, IconLibrary,
  IconSettings, IconLightning, IconChevron, IconCheck, IconLogout,
  IconMegaphone, IconLifeBuoy, IconInbox,
} from './icons.jsx';

const ASSET_ROUTES = ['brand-research', 'marketing-brief', 'funnels', 'videos', 'library'];

const NavItem = ({ icon: Ic, label, sub, active, onClick, badge }) => (
  <button onClick={onClick} aria-current={active ? 'page' : undefined} style={{
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '10px 12px',
    background: active ? '#FFF' : 'transparent',
    color: active ? PALETTE.ink : PALETTE.ink2,
    borderRadius: 10, cursor: 'pointer', textAlign: 'left',
    fontFamily: 'inherit', fontSize: 13.5, fontWeight: active ? 500 : 400,
    border: active ? `1px solid ${PALETTE.controlBorder}` : '1px solid transparent',
    boxShadow: active ? '0 1px 2px rgba(26,23,20,0.04)' : 'none',
    transition: 'all .15s',
  }}>
    <Ic size={17} />
    <span style={{ flex: 1 }}>{label}</span>
    {sub && <span style={{ fontSize: 11, color: PALETTE.ink3 }}>{sub}</span>}
    {badge && <Pill tone="live" style={{ padding: '2px 7px', fontSize: 10 }}><LiveDot/> {badge}</Pill>}
  </button>
);

const CompactNavItem = ({ icon: Ic, label, active, onClick }) => (
  <button onClick={onClick} aria-current={active ? 'page' : undefined} style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 36,
    padding: '8px 10px',
    background: active ? '#FFF' : 'transparent',
    color: active ? PALETTE.ink : PALETTE.ink2,
    borderRadius: 9,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: active ? 500 : 400,
    border: active ? `1px solid ${PALETTE.controlBorder}` : '1px solid transparent',
  }}>
    <Ic size={16} />
    <span>{label}</span>
  </button>
);

export default function Sidebar({
  current,
  onNav,
  businessName = 'Not set up yet',
  userName = 'Account',
  compact = false,
  isPlatformAdmin = false,
  hasTenantAccess = true,
  canReadLeads = false,
  canCreateCampaign = true,
  mvpMode = false,
  legacyCreationRetired = false,
  platformCapabilities = [],
  platformView = '',
  productPolicy = null,
  companySwitcher = null,
  onLogout = () => {},
}) {
  // Auto-expand the Assets group when the user is on one of its sub-routes
  // (e.g. arriving via direct hash link). Otherwise default-collapsed so the
  // sidebar stays clean.
  const [assetsOpen, setAssetsOpen] = useState(ASSET_ROUTES.includes(current));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const assetActive = ASSET_ROUTES.includes(current);
  const mvpRouteActive = (route) => !mvpMode || tenantRouteDecision(productPolicy, route).ok;
  const beyondLeadsBrand = mvpMode || isPlatformAdmin;
  const platformBrandContext = isPlatformAdmin && current === 'platform';
  const brandSubtitle = platformBrandContext
    ? 'Platform operations'
    : mvpMode ? 'Curated Meta ads' : isPlatformAdmin ? 'Platform operations' : 'Lead Platform';
  const exactPlatformCapabilities = Array.isArray(platformCapabilities) ? platformCapabilities : [];
  const canManagePlatformCompanies = isPlatformAdmin
    && exactPlatformCapabilities.includes('platform.companies.manage');
  const canManagePlatformUsers = isPlatformAdmin && exactPlatformCapabilities.includes('platform.users.read');
  const canManagePlatformCatalog = isPlatformAdmin
    && exactPlatformCapabilities.includes('platform.catalog.manage');
  const platformOpsActive = current === 'platform' && !['companies', 'catalog', 'users'].includes(platformView);
  const campaignButtonStyle = mvpMode
    ? { background: 'var(--brand-gold)', color: 'var(--brand-charcoal)', border: '1px solid var(--brand-focus)' }
    : { background: PALETTE.accent, color: '#fff', border: `1px solid ${PALETTE.accentDeep}` };

  if (compact) {
    return (
      <aside style={{
        width: '100%',
        background: PALETTE.surfaceAlt,
        borderBottom: `1px solid ${PALETTE.line}`,
        padding: '10px 10px 8px',
        fontFamily: beyondLeadsBrand ? 'Hanken Grotesk, sans-serif' : 'Inter, sans-serif',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {beyondLeadsBrand ? (
            <BrandIdentity
              detail={brandSubtitle}
              size="sm"
              className="bl-sidebar-brand-identity"
            />
          ) : (
            <>
              <div style={{
                width: 28, height: 28, borderRadius: 7, background: PALETTE.ink,
                color: PALETTE.accent, display: 'grid', placeItems: 'center',
                fontFamily: 'Instrument Serif, serif', fontSize: 18, fontStyle: 'italic',
                flex: '0 0 auto',
              }}>r</div>
              <div style={{ flex: 1, minWidth: 0, lineHeight: 1.15 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Remodel AI</div>
                <div style={{ fontSize: 11, color: PALETTE.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{businessName}</div>
              </div>
            </>
          )}
          {mvpMode && (
            <button
              type="button"
              aria-label="Toggle workspace navigation"
              aria-expanded={mobileMenuOpen}
              aria-controls="beyondleads-mobile-navigation"
              onClick={() => setMobileMenuOpen((open) => !open)}
              style={{
                minHeight: 34, padding: '7px 9px', border: `1px solid ${PALETTE.controlBorder}`,
                borderRadius: 9, background: PALETTE.surface, color: PALETTE.ink,
                cursor: 'pointer', fontSize: 12, fontWeight: 700,
              }}
            >
              Menu
            </button>
          )}
          <button
            type="button"
            aria-label="Open account profile"
            onClick={() => onNav('profile')}
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              border: current === 'profile' ? `1px solid ${PALETTE.controlBorder}` : '1px solid transparent',
              background: current === 'profile' ? PALETTE.surface : 'transparent',
              color: PALETTE.ink2,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              flex: '0 0 auto',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
            }}
            title="Account profile"
          >
            {(userName[0] || 'A').toUpperCase()}
          </button>
          {hasTenantAccess && (!mvpMode
            ? !legacyCreationRetired
            : (canCreateCampaign && mvpRouteActive('curated-campaign'))) && (
            <button onClick={() => onNav(mvpMode ? 'curated-campaign' : 'wizard')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 34,
              padding: '7px 10px', ...campaignButtonStyle, borderRadius: 9,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5,
              fontWeight: 500, flex: '0 0 auto',
            }}>
              <IconLightning size={14} />
              New
            </button>
          )}
        </div>

        {companySwitcher}
        {(!mvpMode || mobileMenuOpen) && <div id="beyondleads-mobile-navigation" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', overflowX: 'visible', paddingTop: 10 }}>
          {hasTenantAccess && (
            <>
              {mvpRouteActive('home') && <CompactNavItem icon={IconHome} label="Home" active={current === 'home'} onClick={() => onNav('home')} />}
              {mvpRouteActive('campaigns') && <CompactNavItem icon={IconMegaphone} label="Campaigns" active={current === 'campaigns' || current === 'dashboard' || current === 'campaign-review' || current === 'workflow'} onClick={() => onNav('campaigns')} />}
              {canReadLeads && mvpRouteActive('leads') && <CompactNavItem icon={IconInbox} label="Leads" active={current === 'leads'} onClick={() => onNav('leads')} />}
              {!mvpMode && <CompactNavItem icon={IconChart} label="Reports" active={current === 'reports' || current === 'live-campaigns'} onClick={() => onNav('reports')} />}
              {!mvpMode && <CompactNavItem icon={IconLibrary} label="Assets" active={assetActive} onClick={() => onNav('library')} />}
            </>
          )}
          {hasTenantAccess && !mvpMode && (
            <>
              <CompactNavItem icon={IconCheck} label="Onboarding" active={current === 'onboarding'} onClick={() => onNav('onboarding')} />
              <CompactNavItem icon={IconLifeBuoy} label="Recovery" active={current === 'recovery'} onClick={() => onNav('recovery')} />
              {mvpRouteActive('company') && <CompactNavItem icon={IconUsers} label="Company" active={current === 'company'} onClick={() => onNav('company')} />}
              <CompactNavItem icon={IconSettings} label="Settings" active={current === 'settings'} onClick={() => onNav('settings')} />
            </>
          )}
          {hasTenantAccess && mvpMode && (
            <>
              {mvpRouteActive('company') && <CompactNavItem icon={IconUsers} label="Company" active={current === 'company'} onClick={() => onNav('company')} />}
              {mvpRouteActive('billing') && <CompactNavItem icon={IconSettings} label="Billing" active={current === 'billing'} onClick={() => onNav('billing')} />}

            </>
          )}
          {isPlatformAdmin && (
            <>
              {canManagePlatformCompanies && <CompactNavItem icon={IconUsers} label="Company Management" active={current === 'platform' && platformView === 'companies'} onClick={() => onNav('platform/companies')} />}
              {canManagePlatformCatalog && <CompactNavItem icon={IconLibrary} label="Creative Catalog" active={current === 'platform' && platformView === 'catalog'} onClick={() => onNav('platform/catalog')} />}
          {canManagePlatformUsers && <CompactNavItem icon={IconUsers} label="Users" active={current === 'platform' && platformView === 'users'} onClick={() => onNav('platform/users')} />}
              <CompactNavItem icon={IconSettings} label="Platform Ops" active={platformOpsActive} onClick={() => onNav('platform')} />
            </>
          )}
          <CompactNavItem icon={IconLogout} label="Sign out" active={false} onClick={onLogout} />
        </div>}
      </aside>
    );
  }

  return (
    <aside style={{
      width: mvpMode ? 276 : 248, background: PALETTE.surfaceAlt, borderRight: `1px solid ${PALETTE.line}`,
      padding: '18px 14px', display: 'flex', flexDirection: 'column', gap: 2,
      fontFamily: beyondLeadsBrand ? 'Hanken Grotesk, sans-serif' : 'Inter, sans-serif', height: '100vh', overflowY: 'auto',
      flex: mvpMode ? '0 0 auto' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 14px', minWidth: 0 }}>
        {beyondLeadsBrand ? (
          <BrandIdentity detail={brandSubtitle} className="bl-sidebar-brand-identity" />
        ) : (
          <>
            <div style={{
              width: 28, height: 28, borderRadius: 7, background: PALETTE.ink,
              color: PALETTE.accent, display: 'grid', placeItems: 'center',
              fontFamily: 'Instrument Serif, serif', fontSize: 18, fontStyle: 'italic',
            }}>r</div>
            <div style={{ lineHeight: 1.1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.ink, letterSpacing: '-0.01em' }}>Remodel AI</div>
              <div style={{ fontSize: 10.5, color: PALETTE.ink3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Lead Platform</div>
            </div>
          </>
        )}
      </div>

      {companySwitcher}

      {hasTenantAccess && (!mvpMode
        ? !legacyCreationRetired
        : (canCreateCampaign && mvpRouteActive('curated-campaign'))) && (
        <button onClick={() => onNav(mvpMode ? 'curated-campaign' : 'wizard')} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px', margin: '0 0 16px',
          ...campaignButtonStyle,
          borderRadius: 10, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500,
          boxShadow: '0 1px 0 rgba(184,80,26,0.6) inset, 0 1px 3px rgba(184,80,26,0.15)',
        }}>
          <IconLightning size={15} />
          <span style={{ flex: 1, textAlign: 'left' }}>New campaign</span>
          {!mvpMode && <span style={{ fontSize: 10.5, opacity: 0.9, background: 'rgba(255,255,255,0.18)', padding: '2px 6px', borderRadius: 4 }}>{'10 min'}</span>}
        </button>
      )}

      {hasTenantAccess && (
        <>
          <div style={{ padding: '0 8px 6px', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: PALETTE.ink3 }}>Workspace</div>
          {mvpRouteActive('home') && <NavItem icon={IconHome} label="Home" active={current === 'home'} onClick={() => onNav('home')} />}
          {mvpRouteActive('campaigns') && <NavItem icon={IconMegaphone} label="Campaigns" active={current === 'campaigns' || current === 'dashboard' || current === 'campaign-review' || current === 'workflow'} onClick={() => onNav('campaigns')} />}
          {canReadLeads && mvpRouteActive('leads') && <NavItem icon={IconInbox} label="Leads" active={current === 'leads'} onClick={() => onNav('leads')} />}
          {!mvpMode && <NavItem icon={IconChart} label="Reports" active={current === 'reports' || current === 'live-campaigns'} onClick={() => onNav('reports')} />}

          {/* Assets — collapsible group. Click toggles open; clicking a sub-item also navigates. */}
          {!mvpMode && !legacyCreationRetired && <button
            onClick={() => setAssetsOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%',
              padding: '10px 12px',
              background: assetActive && !assetsOpen ? '#FFF' : 'transparent',
              color: assetActive ? PALETTE.ink : PALETTE.ink2,
              borderRadius: 10, cursor: 'pointer', textAlign: 'left',
              fontFamily: 'inherit', fontSize: 13.5,
              fontWeight: assetActive ? 500 : 400,
              border: assetActive && !assetsOpen ? `1px solid ${PALETTE.line}` : '1px solid transparent',
              transition: 'all .15s',
            }}
          >
            <IconLibrary size={17} />
            <span style={{ flex: 1 }}>Assets</span>
            <span style={{
              display: 'inline-flex', transition: 'transform .15s',
              transform: assetsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              color: PALETTE.ink3,
            }}>
              <IconChevron size={12} />
            </span>
          </button>}
          {!mvpMode && !legacyCreationRetired && assetsOpen && (
            <div style={{ marginLeft: 14, paddingLeft: 8, borderLeft: `1px solid ${PALETTE.lineSoft || PALETTE.line}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <NavItem icon={IconUsers}   label="Brand research"  active={current === 'brand-research'}  onClick={() => onNav('brand-research')} />
              <NavItem icon={IconQuiz}    label="Marketing brief" active={current === 'marketing-brief'} onClick={() => onNav('marketing-brief')} />
              <NavItem icon={IconQuiz}    label="Funnels"         active={current === 'funnels'}         onClick={() => onNav('funnels')} />
              <NavItem icon={IconVideo}   label="Videos"          active={current === 'videos'}          onClick={() => onNav('videos')} />
              <NavItem icon={IconLibrary} label="Library"         active={current === 'library'}         onClick={() => onNav('library')} />
            </div>
          )}

        </>
      )}


      <div style={{ padding: '16px 8px 6px', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: PALETTE.ink3 }}>{mvpMode ? 'Manage' : 'Account'}</div>
      {hasTenantAccess && !mvpMode && (
        <>
          <NavItem icon={IconCheck} label="Onboarding" active={current === 'onboarding'} onClick={() => onNav('onboarding')} />
          <NavItem icon={IconLifeBuoy} label="Recovery" active={current === 'recovery'} onClick={() => onNav('recovery')} />
          <NavItem icon={IconUsers} label="Company" active={current === 'company'} onClick={() => onNav('company')} />
          <NavItem icon={IconSettings} label="Settings" active={current === 'settings'} onClick={() => onNav('settings')} />
        </>
      )}
      {hasTenantAccess && mvpMode && <>
        {mvpRouteActive('company') && <NavItem icon={IconUsers} label="Company" active={current === 'company'} onClick={() => onNav('company')} />}
        {mvpRouteActive('billing') && <NavItem icon={IconSettings} label="Billing" active={current === 'billing'} onClick={() => onNav('billing')} />}
      </>}

      {isPlatformAdmin && (
        <>
          <div style={{ padding: '16px 8px 6px', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: PALETTE.ink3 }}>Platform</div>
          {canManagePlatformCompanies && <NavItem icon={IconUsers} label="Company Management" active={current === 'platform' && platformView === 'companies'} onClick={() => onNav('platform/companies')} />}
          {canManagePlatformCatalog && <NavItem icon={IconLibrary} label="Creative Catalog" active={current === 'platform' && platformView === 'catalog'} onClick={() => onNav('platform/catalog')} />}
          {canManagePlatformUsers && <NavItem icon={IconUsers} label="Users" active={current === 'platform' && platformView === 'users'} onClick={() => onNav('platform/users')} />}
          <NavItem icon={IconSettings} label="Platform Ops" sub="Console" active={platformOpsActive} onClick={() => onNav('platform')} />
        </>
      )}

      <div style={{ flex: 1 }} />

      {hasTenantAccess && !beyondLeadsBrand && (
        <div style={{
          padding: 14, background: PALETTE.surface, border: `1px solid ${PALETTE.line}`,
          borderRadius: 12, fontSize: 12.5, color: PALETTE.ink2, lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 500, color: PALETTE.ink, marginBottom: 2 }}>Need a hand?</div>
          Call your success manager — we pick up.
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: PALETTE.accent, fontWeight: 500 }}>
            (613) 555-0102 <IconChevron size={12} />
          </div>
        </div>
      )}

      <button
        type="button"
        aria-label="Open account profile"
        onClick={() => onNav('profile')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '12px 6px 4px',
          marginTop: 12,
          border: current === 'profile' ? `1px solid ${PALETTE.controlBorder}` : '1px solid transparent',
          borderRadius: 10,
          background: current === 'profile' ? PALETTE.surface : 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: PALETTE.ink, color: '#FAF8F5',
          display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 500,
        }}>{(userName[0] || 'M').toUpperCase()}</div>
        <div style={{ flex: 1, lineHeight: 1.15, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: PALETTE.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
          <div style={{ fontSize: 11, color: PALETTE.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{businessName}</div>
        </div>
      </button>
      <button
        type="button"
        onClick={onLogout}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          margin: '8px 6px 0',
          minHeight: 34,
          border: `1px solid ${PALETTE.controlBorder}`,
          borderRadius: 9,
          background: PALETTE.surface,
          color: PALETTE.ink2,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 12.5,
          fontWeight: 500,
        }}
      >
        <IconLogout size={14} />
        Sign out
      </button>
    </aside>
  );
}
